import { useEffect, useRef } from 'react';
import { getSearchQuery } from '../../../utils/query';

import { YoutubeIsEmpty, YoutubeProps } from '../youtube.config';
import { YoutubeApiManager } from '../youtube-api-manager';
import { isTrackingEnabled } from '../../../utils/analytics/analytics';
import {
  trackYoutubeVideoComplete,
  trackYoutubeVideoError,
  trackYoutubeVideoMilestoneReached,
  trackYoutubeVideoPause,
  trackYoutubeVideoPlay,
  trackYoutubeVideoPlaybackQualityChange,
  trackYoutubeVideoReady,
  YoutubeAnalyticsData,
} from '../../../utils/analytics/video-analytics';

export const YoutubeEmbed = ({
  id,
  youtubeVideoId = '',
  layout,
  youtubeWidth,
  youtubeHeight,
  youtubeAspectRatio,
  youtubeAutoPlay,
  youtubeLoop,
  youtubeMute,
  youtubePlaysInline = true,
  youtubeRel,
  accessibilityLabel,
  isInEditor,
  cqPath,
}: YoutubeProps) => {
  const youtubeApiManager = useRef<YoutubeApiManager | null>(null);

  useEffect(() => {
    return () => {
      youtubeApiManager.current?.destroy();
    };
  });

  if (YoutubeIsEmpty({ youtubeVideoId, id, isInEditor, cqPath })) {
    return null;
  }

  const analyticsData: YoutubeAnalyticsData = {
    videoId: youtubeVideoId,
    layout,
    title: accessibilityLabel,
    width: youtubeWidth,
    height: youtubeHeight,
    aspectRatio: youtubeAspectRatio,
    autoPlay: youtubeAutoPlay,
    loop: youtubeLoop,
    mute: youtubeMute,
    playInline: youtubePlaysInline,
    rel: youtubeRel,
  };

  const handleRef = (node?: HTMLIFrameElement | null) => {
    if (node) {
      youtubeApiManager.current = new YoutubeApiManager(node, {
        events: {
          onReady: (e) => trackYoutubeVideoReady(analyticsData, e.target),
          onPlay: (e) => trackYoutubeVideoPlay(analyticsData, e.target),
          onPause: (e) => trackYoutubeVideoPause(analyticsData, e.target),
          onComplete: (e) => trackYoutubeVideoComplete(analyticsData, e.target),
          onMilestoneReached: (e) => trackYoutubeVideoMilestoneReached(analyticsData, e.data, e.target),
          onPlaybackQualityChange: (e) => trackYoutubeVideoPlaybackQualityChange(analyticsData, e.data, e.target),
          onError: (e) => trackYoutubeVideoError(analyticsData, e.data, e.target),
        },
      });
    }
  };

  const playerParams = Object.entries({
    youtubeAutoPlay,
    youtubeLoop,
    youtubeMute,
    youtubePlaysInline,
    youtubeRel,
    enablejsapi: isTrackingEnabled(),
  }).map(([key, value]) => {
    const suffix = key.replace('youtube', '').toLowerCase();
    const propValue = value ? '1' : '0';

    return `${suffix}=${propValue}`;
  });

  const queryparams = getSearchQuery(playerParams);

  const encodedVideoId = encodeURIComponent(youtubeVideoId);

  return (
    <iframe
      tabIndex={0}
      ref={handleRef}
      data-testid="snowflake-youtube-embedded"
      className="snowflake-youtube-embedded"
      title={`Youtube Video ${youtubeVideoId}`}
      src={`https://www.youtube.com/embed/${encodedVideoId}?playlist=${encodedVideoId}&${queryparams}`}
      allowFullScreen
      width={youtubeWidth}
      height={youtubeHeight}
      style={{ maxHeight: `${youtubeHeight}px`, maxWidth: `${youtubeWidth}px` }}
      allow="autoplay; fullscreen"
      aria-label={accessibilityLabel}
    ></iframe>
  );
};

export default YoutubeEmbed;
